import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import './CompanySettings.css';

/**
 * Company Settings - Admin panel to edit homepage content
 */
const CompanySettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/api/company/settings');
      setSettings(response.data.settings);
    } catch (err) {
      console.error('Failed to load settings:', err);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/api/company/settings', settings);

      setMessage({ type: 'success', text: 'Settings saved successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const updateHero = (field, value) => {
    setSettings({
      ...settings,
      hero: {
        ...settings.hero,
        [field]: value
      }
    });
  };

  const updateWhyJoin = (field, value) => {
    setSettings({
      ...settings,
      whyJoin: {
        ...settings.whyJoin,
        [field]: value
      }
    });
  };

  const updatePoint = (index, field, value) => {
    const newPoints = [...settings.whyJoin.points];
    newPoints[index] = {
      ...newPoints[index],
      [field]: value
    };
    updateWhyJoin('points', newPoints);
  };

  const addPoint = () => {
    const newPoints = [...(settings.whyJoin.points || []), { title: '', description: '' }];
    updateWhyJoin('points', newPoints);
  };

  const removePoint = (index) => {
    const newPoints = settings.whyJoin.points.filter((_, i) => i !== index);
    updateWhyJoin('points', newPoints);
  };

  const updateEmployeeSpotlight = (field, value) => {
    setSettings({
      ...settings,
      employeeSpotlight: {
        ...settings.employeeSpotlight,
        [field]: value
      }
    });
  };

  const updateVideo = (index, field, value) => {
    const newVideos = [...(settings.employeeSpotlight?.videos || [])];
    newVideos[index] = {
      ...newVideos[index],
      [field]: value
    };
    updateEmployeeSpotlight('videos', newVideos);
  };

  const addVideo = () => {
    const newVideos = [...(settings.employeeSpotlight?.videos || []), {
      title: '',
      employeeName: '',
      employeeRole: '',
      videoUrl: '',
      thumbnail: '',
      order: (settings.employeeSpotlight?.videos || []).length
    }];
    updateEmployeeSpotlight('videos', newVideos);
  };

  const removeVideo = (index) => {
    const newVideos = (settings.employeeSpotlight?.videos || []).filter((_, i) => i !== index);
    // Re-assign order values after removal
    const reorderedVideos = newVideos.map((video, idx) => ({ ...video, order: idx }));
    updateEmployeeSpotlight('videos', reorderedVideos);
  };

  const moveVideoUp = (index) => {
    if (index === 0) return;
    const newVideos = [...(settings.employeeSpotlight?.videos || [])];
    [newVideos[index - 1], newVideos[index]] = [newVideos[index], newVideos[index - 1]];
    // Update order values
    const reorderedVideos = newVideos.map((video, idx) => ({ ...video, order: idx }));
    updateEmployeeSpotlight('videos', reorderedVideos);
  };

  const moveVideoDown = (index) => {
    const videos = settings.employeeSpotlight?.videos || [];
    if (index === videos.length - 1) return;
    const newVideos = [...videos];
    [newVideos[index], newVideos[index + 1]] = [newVideos[index + 1], newVideos[index]];
    // Update order values
    const reorderedVideos = newVideos.map((video, idx) => ({ ...video, order: idx }));
    updateEmployeeSpotlight('videos', reorderedVideos);
  };

  if (loading) {
    return <div className="company-settings loading">Loading settings...</div>;
  }

  return (
    <div className="company-settings">
      <div className="settings-header">
        <h1>Company Settings</h1>
        <p>Edit the homepage content that visitors see</p>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Hero Section */}
      <section className="settings-section">
        <h2>Hero Section</h2>

        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={settings.hero?.title || ''}
            onChange={(e) => updateHero('title', e.target.value)}
            placeholder="SecureLayer7"
          />
        </div>

        <div className="form-group">
          <label>Tagline</label>
          <input
            type="text"
            value={settings.hero?.tagline || ''}
            onChange={(e) => updateHero('tagline', e.target.value)}
            placeholder="Find vulnerabilities before attackers do"
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={settings.hero?.description || ''}
            onChange={(e) => updateHero('description', e.target.value)}
            placeholder="Company description..."
            rows="4"
          />
        </div>
      </section>

      {/* Why Join Section */}
      <section className="settings-section">
        <div className="section-header">
          <h2>Why Join Section</h2>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.whyJoin?.enabled || false}
              onChange={(e) => updateWhyJoin('enabled', e.target.checked)}
            />
            <span>Show this section</span>
          </label>
        </div>

        {settings.whyJoin?.enabled && (
          <>
            <div className="form-group">
              <label>Section Title</label>
              <input
                type="text"
                value={settings.whyJoin?.title || ''}
                onChange={(e) => updateWhyJoin('title', e.target.value)}
                placeholder="Why Join Us"
              />
            </div>

            <div className="points-editor">
              <div className="points-header">
                <h3>Points</h3>
                <button onClick={addPoint} className="add-point-btn">
                  + Add Point
                </button>
              </div>

              {settings.whyJoin.points?.map((point, idx) => (
                <div key={idx} className="point-editor">
                  <div className="point-number">{idx + 1}</div>
                  <div className="point-fields">
                    <input
                      type="text"
                      value={point.title}
                      onChange={(e) => updatePoint(idx, 'title', e.target.value)}
                      placeholder="Point title"
                    />
                    <textarea
                      value={point.description}
                      onChange={(e) => updatePoint(idx, 'description', e.target.value)}
                      placeholder="Point description"
                      rows="2"
                    />
                  </div>
                  <button
                    onClick={() => removePoint(idx)}
                    className="remove-point-btn"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Employee Spotlight Section */}
      <section className="settings-section">
        <div className="section-header">
          <h2>Employee Spotlight</h2>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.employeeSpotlight?.enabled || false}
              onChange={(e) => updateEmployeeSpotlight('enabled', e.target.checked)}
            />
            <span>Show this section</span>
          </label>
        </div>

        {settings.employeeSpotlight?.enabled && (
          <>
            <div className="form-group">
              <label>Section Title</label>
              <input
                type="text"
                value={settings.employeeSpotlight?.title || ''}
                onChange={(e) => updateEmployeeSpotlight('title', e.target.value)}
                placeholder="Employee Spotlight"
              />
            </div>

            <div className="points-editor">
              <div className="points-header">
                <h3>Videos</h3>
                <button onClick={addVideo} className="add-point-btn">
                  + Add Video
                </button>
              </div>

              {settings.employeeSpotlight?.videos?.map((video, idx) => (
                <div key={idx} className="point-editor video-editor">
                  <div className="video-order-controls">
                    <button
                      onClick={() => moveVideoUp(idx)}
                      disabled={idx === 0}
                      className="order-btn"
                      title="Move up"
                    >
                      ▲
                    </button>
                    <div className="point-number">{idx + 1}</div>
                    <button
                      onClick={() => moveVideoDown(idx)}
                      disabled={idx === settings.employeeSpotlight?.videos?.length - 1}
                      className="order-btn"
                      title="Move down"
                    >
                      ▼
                    </button>
                  </div>
                  <div className="point-fields">
                    <input
                      type="text"
                      value={video.employeeName || ''}
                      onChange={(e) => updateVideo(idx, 'employeeName', e.target.value)}
                      placeholder="Employee Name"
                    />
                    <input
                      type="text"
                      value={video.employeeRole || ''}
                      onChange={(e) => updateVideo(idx, 'employeeRole', e.target.value)}
                      placeholder="Employee Role (e.g., Senior Security Engineer)"
                    />
                    <input
                      type="text"
                      value={video.title || ''}
                      onChange={(e) => updateVideo(idx, 'title', e.target.value)}
                      placeholder="Video Title"
                    />
                    <input
                      type="url"
                      value={video.videoUrl || ''}
                      onChange={(e) => updateVideo(idx, 'videoUrl', e.target.value)}
                      placeholder="Video URL (YouTube, Vimeo, or LinkedIn)"
                    />
                    <small style={{ color: '#737373', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                      <strong>Supported platforms only:</strong><br/>
                      • YouTube: https://www.youtube.com/watch?v=...<br/>
                      • Vimeo: https://vimeo.com/123456789<br/>
                      • LinkedIn: Full post URL with urn:li:activity:...<br/>
                      <span style={{ color: '#dc2626' }}>Other URLs will not be displayed</span>
                    </small>
                    <input
                      type="url"
                      value={video.thumbnail || ''}
                      onChange={(e) => updateVideo(idx, 'thumbnail', e.target.value)}
                      placeholder="Custom Thumbnail URL (optional)"
                    />
                  </div>
                  <button
                    onClick={() => removeVideo(idx)}
                    className="remove-point-btn"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Save Button */}
      <div className="settings-footer">
        <button
          onClick={handleSave}
          disabled={saving}
          className="save-btn"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default CompanySettings;
